import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FAF8F5] to-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="mb-6 border-[#6B0B0B] text-[#6B0B0B] hover:bg-[#6B0B0B] hover:text-white"
        >
          <ArrowLeft className="mr-2" size={18} />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-lg border border-[#D4AF37]/20 p-8 md:p-12">
          <h1 className="text-4xl font-bold text-[#6B0B0B] mb-2">Privacy Policy</h1>
          <p className="text-gray-600 mb-6">How we collect, use and protect your personal information (POPIA)</p>
          <p className="text-sm text-gray-500 mb-8">Last updated: 23 October 2026</p>

          <div className="prose prose-lg max-w-none">
            <div className="bg-[#FFF9F0] border-l-4 border-[#D4AF37] p-6 mb-8 rounded-r-lg">
              <h2 className="text-xl font-semibold text-[#0B090A] mt-0">The short version</h2>
              <ul className="text-gray-700 space-y-2">
                <li>We collect only what we need to take your booking, run your tour safely, and meet our legal obligations.</li>
                <li>We do not sell your personal information. We share it only with the people who help us deliver your booking and where the law requires it.</li>
                <li>You can ask to see, correct or delete your information, object to how we use it, or opt out of marketing at any time.</li>
              </ul>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">1. Who we are (the Responsible Party)</h2>
              <p className="text-gray-700 leading-relaxed mb-3">The responsible party for your personal information is Cape Atlantic Tours, a company registered in South Africa.</p>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Email:</strong> info@capeatiantictours.co.za</li>
                <li><strong>Telephone:</strong> 073 654 0090</li>
                <li><strong>Website:</strong> capeatiantictours.co.za</li>
                <li><strong>Information Officer:</strong> George Kagiso Monamodi (Director)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">2. What personal information we collect</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 mb-6">
                  <thead>
                    <tr className="bg-[#FAF8F5]">
                      <th className="border border-gray-300 p-3 text-left font-semibold">Category</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Examples</th>
                      <th className="border border-gray-300 p-3 text-left font-semibold">Why we need it</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr>
                      <td className="border border-gray-300 p-3">Identity and contact details</td>
                      <td className="border border-gray-300 p-3">Name, email, phone, country, nationality</td>
                      <td className="border border-gray-300 p-3">To create and confirm your booking</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Identity documents</td>
                      <td className="border border-gray-300 p-3">SA ID number or passport number</td>
                      <td className="border border-gray-300 p-3">To verify resident vs international pricing</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Booking details</td>
                      <td className="border border-gray-300 p-3">Tour, date, guests, special requests</td>
                      <td className="border border-gray-300 p-3">To plan and deliver the tour</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Payment details</td>
                      <td className="border border-gray-300 p-3">Payment status, transaction reference</td>
                      <td className="border border-gray-300 p-3">To process payments and refunds</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">3. How we collect it</h2>
              <ul className="text-gray-700 space-y-2 list-disc pl-6">
                <li>Directly from you when you complete booking or contact forms</li>
                <li>Automatically through cookies when you use our website</li>
                <li>From third parties (payment providers, travel agents) where relevant</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">4. Why we use your information</h2>
              <ul className="text-gray-700 space-y-3 list-disc pl-6">
                <li><strong>Taking and delivering bookings:</strong> Necessary to carry out our contract with you</li>
                <li><strong>Processing payments:</strong> Contract and legal obligation</li>
                <li><strong>Safety and emergency contact:</strong> Legal obligation and legitimate interests</li>
                <li><strong>Website security:</strong> Legitimate interests</li>
                <li><strong>Marketing:</strong> Your consent or existing-customer rules</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">5. Direct marketing</h2>
              <p className="text-gray-700 leading-relaxed mb-3">We send you marketing only if you have opted in, or if you are an existing customer and we are marketing similar services.</p>
              <p className="text-gray-700 leading-relaxed">Every marketing message includes a simple way to unsubscribe. You can also opt out by emailing info@capeatiantictours.co.za</p>
              <p className="text-gray-700 leading-relaxed font-semibold mt-3">We do not sell or rent your personal information.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">6. Who we share your information with</h2>
              <ul className="text-gray-700 space-y-2 list-disc pl-6">
                <li>Service providers (hosting, payment, email) who process on our behalf</li>
                <li>Tour partners and guides who need it to deliver your tour</li>
                <li>Professional advisers (accountants, lawyers)</li>
                <li>Authorities and courts where the law requires it</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">7. How we keep your information safe</h2>
              <p className="text-gray-700 leading-relaxed">We take reasonable steps to protect personal information including encrypted (HTTPS) connections, access controls, secure payment processing, and regular system reviews.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">8. How long we keep your information</h2>
              <ul className="text-gray-700 space-y-2 list-disc pl-6">
                <li>Booking and payment records: At least 5 years (tax requirements)</li>
                <li>Identity document details: Only as long as needed to verify booking</li>
                <li>Marketing preferences: Until you opt out</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">9. Your rights</h2>
              <p className="text-gray-700 leading-relaxed mb-3">Under POPIA you have the right to:</p>
              <ul className="text-gray-700 space-y-2 list-disc pl-6">
                <li>Ask us to confirm whether we hold your information and to access it</li>
                <li>Ask us to correct or delete inaccurate information</li>
                <li>Object to our processing of your information</li>
                <li>Withdraw your consent at any time</li>
                <li>Lodge a complaint with the Information Regulator</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">To exercise these rights, email info@capeatiantictours.co.za</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">10. Complaints to the Information Regulator</h2>
              <p className="text-gray-700 leading-relaxed mb-3">Please contact us first. If you are not satisfied, you can complain to:</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Information Regulator (South Africa)</strong></p>
                <p className="text-gray-700">Email: enquiries@inforegulator.org.za</p>
                <p className="text-gray-700">Telephone: 010 023 5200 or 0800 017 160</p>
                <p className="text-gray-700">Website: inforegulator.org.za</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B090A] mb-4 border-b-2 border-[#6B0B0B] pb-2">11. Contact us</h2>
              <div className="bg-[#FFF9F0] p-6 rounded-lg">
                <p className="text-gray-700"><strong>Information Officer:</strong> George Kagiso Monamodi</p>
                <p className="text-gray-700"><strong>Email:</strong> info@capeatiantictours.co.za</p>
                <p className="text-gray-700"><strong>Telephone:</strong> 073 654 0090</p>
                <p className="text-gray-700"><strong>Cape Atlantic Tours</strong></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;